import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '@/lib/api';
import {
  applySessionMeta,
  clearSessionMeta,
  subscribeSession,
  getSessionMeta,
  getSessionStatus,
  extendSession as extendSessionLib,
} from '@/lib/authSession';

const AuthContext = createContext(null);

const RETURN_URL_KEY = 'omnisys_auth_return_url';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionStatus, setSessionStatus] = useState('active');
  const [sessionMeta, setSessionMetaState] = useState(null);
  const [sessionExpiredOpen, setSessionExpiredOpen] = useState(false);

  const syncSessionFromResponse = useCallback((data) => {
    if (data?.session) {
      applySessionMeta(data.session);
      setSessionMetaState(data.session);
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const res = await authAPI.me();
      setUser(res.data);
      syncSessionFromResponse(res.data);
    } catch {
      setUser(null);
      clearSessionMeta();
      setSessionMetaState(null);
    } finally {
      setLoading(false);
    }
  }, [syncSessionFromResponse]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    return subscribeSession((event, payload) => {
      if (event === 'meta') {
        setSessionMetaState(payload);
      }
      if (event === 'status') {
        setSessionStatus(payload);
      }
      if (event === 'session-expired') {
        setUser(null);
        clearSessionMeta();
        setSessionMetaState(null);
        setSessionStatus('expired');
        try {
          const path = window.location.pathname + window.location.search;
          if (path && path !== '/login') {
            sessionStorage.setItem(RETURN_URL_KEY, path);
          }
        } catch (_) { /* ignore */ }
        setSessionExpiredOpen(true);
      }
    });
  }, []);

  // **Con la contraseña correcta todavía no se entra si la cuenta es local** (2026-10-13): el
  // servidor devuelve `segundo_factor` y un permiso temporal, y la pantalla de login pide el código.
  // Recién `completarLogin` deja al usuario adentro.
  const login = async (email, password) => {
    const res = await authAPI.login(email, password);
    if (res.data?.segundo_factor) return res.data;
    setUser(res.data);
    syncSessionFromResponse(res.data);
    return res.data;
  };

  const completarLogin = (datos) => {
    setUser(datos);
    syncSessionFromResponse(datos);
    return datos;
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (_) {
      /* cookie puede estar expirada */
    }
    setUser(null);
    clearSessionMeta();
    setSessionMetaState(null);
    setSessionExpiredOpen(false);
  };

  const extendSession = async () => {
    const res = await extendSessionLib(() => authAPI.refresh());
    if (res?.data?.session) {
      setSessionMetaState(res.data.session);
      setSessionStatus(getSessionStatus());
    }
    return res;
  };

  const consumeReturnUrl = () => {
    try {
      const url = sessionStorage.getItem(RETURN_URL_KEY);
      if (url) sessionStorage.removeItem(RETURN_URL_KEY);
      return url;
    } catch (_) {
      return null;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        completarLogin,
        logout,
        checkAuth,
        sessionStatus,
        sessionMeta: sessionMeta || getSessionMeta(),
        extendSession,
        sessionExpiredOpen,
        setSessionExpiredOpen,
        consumeReturnUrl,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}
